/**
 * Сторінка моїх записів клієнта
 * @component MyRecordsPage
 * @file MyRecordsPage.tsx
 * @path frontend/src/pages/MyRecordsPage.tsx
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Tooltip } from '@mui/material';
import { useNotification } from '../../contexts/NotificationContext';
import { openGoogleMaps } from '../../utils/mapsUtils';
import CancelAppointmentModal from '../../components/CancelAppointmentModal';
import ReviewModal from '../../components/ReviewModal';
import WorkingHoursModal from '../../modals/WorkingHoursModal';
import './style.css';

// Calendar Tab Component
interface CalendarTabProps {
  appointments: Appointment[];
  language: string;
  colors: any;
  onLocationClick: (location: string) => void;
}

const CalendarTab: React.FC<CalendarTabProps> = ({ appointments, language, colors, onLocationClick }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const getAppointmentsForDate = (date: Date) => {
    if (!date) return [];
    
    return appointments.filter(appointment => {
      const appointmentDate = new Date(appointment.date);
      return appointmentDate.toDateString() === date.toDateString();
    });
  };

  const getDayAppointmentStatus = (date: Date) => {
    const dayAppointments = getAppointmentsForDate(date);
    if (dayAppointments.length === 0) return null;
    
    const hasUpcoming = dayAppointments.some(app => app.status === 'upcoming');
    const hasCompleted = dayAppointments.some(app => app.status === 'completed');
    
    if (hasUpcoming && hasCompleted) return 'mixed';
    if (hasUpcoming) return 'upcoming';
    if (hasCompleted) return 'completed';
    return null;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString(language === 'ua' ? 'uk-UA' : 'en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(newMonth.getMonth() - 1);
      } else {
        newMonth.setMonth(newMonth.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const days = getDaysInMonth(currentMonth);
  const weekDays = language === 'ua' 
    ? ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="calendar-tab">
      <div className="calendar-header">
        <button 
          className="calendar-nav-btn"
          onClick={() => navigateMonth('prev')}
        >
          <i className="bi bi-chevron-left"></i>
        </button>
        <h3>{formatDate(currentMonth)}</h3>
        <button 
          className="calendar-nav-btn"
          onClick={() => navigateMonth('next')}
        >
          <i className="bi bi-chevron-right"></i>
        </button>
      </div>

      <div className="calendar-grid">
        {/* Week day headers */}
        {weekDays.map((day, index) => (
          <div key={index} className="calendar-weekday">
            {day}
          </div>
        ))}
        
        {/* Calendar days */}
        {days.map((date, index) => {
          if (!date) {
            return <div key={index} className="calendar-day empty"></div>;
          }

          const dayAppointments = getAppointmentsForDate(date);
          const isSelected = selectedDate?.toDateString() === date.toDateString();
          const isToday = new Date().toDateString() === date.toDateString();
          const hasAppointments = dayAppointments.length > 0;
          const appointmentStatus = getDayAppointmentStatus(date);

          return (
            <div
              key={index}
              className={`calendar-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${hasAppointments ? `has-appointments ${appointmentStatus ? `status-${appointmentStatus}` : ''}` : ''}`}
              onClick={() => setSelectedDate(date)}
            >
              <div className="calendar-day-number">
                {date.getDate()}
                {hasAppointments && (
                  <span className="appointment-indicator">
                    <i className="bi bi-dot"></i>
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Selected date appointments */}
      {selectedDate && (
        <div className="selected-date-appointments">
          <h4>
            {selectedDate.toLocaleDateString(language === 'ua' ? 'uk-UA' : 'en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </h4>
          {getAppointmentsForDate(selectedDate).length > 0 ? (
            <div className="appointments-for-date">
              {getAppointmentsForDate(selectedDate).map(appointment => (
                <div key={appointment.id} className="calendar-appointment-card">
                  <div className="appointment-time">
                    <i className="bi bi-clock"></i> {appointment.time}
                  </div>
                  <div className="appointment-details">
                    {appointment.serviceName && <h5>{appointment.serviceName}</h5>}
                    <p>
                      {(() => {
                        if (appointment.isMasterBusiness) {
                          return <><i className="bi bi-person"></i> {appointment.masterName}</>;
                        } else {
                          return <><i className="bi bi-building"></i> {appointment.masterName}</>;
                        }
                      })()}
                    </p>
                    <p 
                      className="location-clickable"
                      onClick={() => appointment.location && onLocationClick(appointment.location)}
                      style={{ 
                        cursor: appointment.location ? 'pointer' : 'default',
                        color: appointment.location ? colors.primary : 'inherit'
                      }}
                    >
                      <i className="bi bi-geo-alt"></i> {appointment.location}
                    </p>
                    {(appointment.masterName !== appointment.salonName ? appointment.masterPhone : appointment.businessPhone) && (
                      <p>
                      <i className="bi bi-telephone" style={{ color: colors.primary }}></i> {
                        (() => {
                          const phone = appointment.masterName !== appointment.salonName ? appointment.masterPhone : appointment.businessPhone;
                          const cleanPhone = phone ? phone.replace(/^\+380/, '') : '';
                          return (
                              <a 
                                href={`tel:+380${cleanPhone}`}
                                style={{ 
                                  color: colors.primary, 
                                  textDecoration: 'none',
                                  fontWeight: 500
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.location.href = `tel:+380${cleanPhone}`;
                                }}
                              >
                                {phone}
                              </a>
                            );
                          })()
                        }
                      </p>
                    )}
                  </div>
                  <div className={`appointment-status ${appointment.status}`}>
                    {appointment.status === 'upcoming' 
                      ? (language === 'ua' ? 'Майбутній' : 'Upcoming')
                      : appointment.status === 'completed'
                      ? (language === 'ua' ? 'Завершений' : 'Completed')
                      : (language === 'ua' ? 'Скасований' : 'Cancelled')
                    }
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-appointments">
              {language === 'ua' ? 'Немає записів на цю дату' : 'No appointments on this date'}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

interface Appointment {
  id: string;
  businessId?: string;
  serviceName: string;
  masterName: string;
  salonName: string;
  date: string;
  time: string;
  status: 'upcoming' | 'completed' | 'cancelled';
  price: number;
  notes?: string;
  masterPhone?: string;
  businessPhone?: string;
  isMasterBusiness?: boolean;
  location?: string;
  hasReview?: boolean;
}

const MyRecordsPage: React.FC = () => {
  const { language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const { colors } = useTheme();
  const { addNotification } = useNotification();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past' | 'calendar'>('upcoming');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<Appointment | null>(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [appointmentToReview, setAppointmentToReview] = useState<Appointment | null>(null);
  const [workingHoursModal, setWorkingHoursModal] = useState<{
    isOpen: boolean;
    masterId: string;
    masterName: string;
    appointmentId?: string;
    isReschedule?: boolean;
  }>({
    isOpen: false,
    masterId: '',
    masterName: '',
    appointmentId: '',
    isReschedule: false,
  });

  // Get token from localStorage
  const getToken = () => localStorage.getItem('auth_token');

  useEffect(() => {
    if (isAuthenticated) {
      loadAppointments();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const loadAppointments = async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Make API calls to get both appointments and reviews
      const [appointmentsResponse, reviewsResponse] = await Promise.all([
        fetch('/api/v1/client/appointments', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
        fetch('/api/v1/reviews', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
      ]);

      if (!appointmentsResponse.ok) {
        throw new Error(`HTTP error! status: ${appointmentsResponse.status}`);
      }

      const appointmentsData = await appointmentsResponse.json();
      let appointments: Appointment[] = appointmentsData.appointments || [];

      // Get reviewed appointment IDs
      let reviewedAppointmentIds: Set<string> = new Set();
      
      if (reviewsResponse.ok) {
        const reviewsData = await reviewsResponse.json();
        const reviews = reviewsData.reviews || [];
        reviewedAppointmentIds = new Set(reviews.map((review: any) => review.appointment_id));
      }

      // Add hasReview flag to appointments
      appointments = appointments.map((apt: any) => ({
        ...apt,
        hasReview: reviewedAppointmentIds.has(apt.id),
        businessId: apt.business_id // Map business_id from backend
      }));

      // Sort appointments by date and time in ascending order
      const sortedAppointments = appointments.sort((a, b) => {
        const dateA = new Date(`${a.date.split('T')[0]}T${a.time}:00`);
        const dateB = new Date(`${b.date.split('T')[0]}T${b.time}:00`);
        return dateA.getTime() - dateB.getTime();
      });

      setAppointments(sortedAppointments);
    } catch (error) {
      console.error('Error loading appointments:', error);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  const upcomingAppointments = appointments.filter(app => app.status === 'upcoming');
  const pastAppointments = appointments.filter(app => app.status !== 'upcoming');

  const currentAppointments = activeTab === 'upcoming' ? upcomingAppointments : pastAppointments;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'ua' ? 'uk-UA' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusText = (status: string) => {
    if (language === 'ua') {
      switch (status) {
        case 'upcoming': return 'Заплановано';
        case 'completed': return 'Виконано';
        case 'cancelled': return 'Скасовано';
        default: return status;
      }
    } else {
      switch (status) {
        case 'upcoming': return 'Upcoming';
        case 'completed': return 'Completed';
        case 'cancelled': return 'Cancelled';
        default: return status;
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming': return '#007bff';
      case 'completed': return '#28a745';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  // Check if appointment can be cancelled
  const canCancelAppointment = (appointment: Appointment) => {
    const appointmentDateTime = new Date(`${appointment.date.split('T')[0]}T${appointment.time}:00`);
    const now = new Date();
    const hoursUntilAppointment = (appointmentDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);

    return {
      canCancel: hoursUntilAppointment > 5,
      canCancelWithContact: hoursUntilAppointment > 3,
      hoursUntilAppointment: hoursUntilAppointment
    };
  };

  // Get cancellation deadline text
  const getCancellationDeadline = (appointment: Appointment) => {
    const appointmentDateTime = new Date(`${appointment.date.split('T')[0]}T${appointment.time}:00`);
    const deadline = new Date(appointmentDateTime.getTime() - 5 * 60 * 60 * 1000); // 5 hours before

    if (language === 'ua') {
      return `Можна скасувати до ${deadline.toLocaleString('uk-UA', {
        day: 'numeric',
        month: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    } else {
      return `Can cancel until ${deadline.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    }
  };

  // Cancel appointment
  const cancelAppointment = async (appointmentId: string) => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`/api/v1/appointments/${appointmentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to cancel appointment');
      }

      // Refresh appointments list
      await loadAppointments();

      addNotification(
        language === 'ua'
          ? '✅ Запис успішно скасовано'
          : '✅ Appointment cancelled successfully',
        'success'
      );
    } catch (error: any) {
      console.error('Error cancelling appointment:', error);
      addNotification(
        error.message || (language === 'ua'
          ? '❌ Помилка при скасуванні запису'
          : '❌ Error cancelling appointment'),
        'error'
      );
    }
  };

  // Open cancel confirmation modal
  const openCancelModal = (appointment: Appointment) => {
    setAppointmentToCancel(appointment);
    setCancelModalOpen(true);
  };

  // Handle confirmed cancellation
  const handleConfirmCancel = async () => {
    if (appointmentToCancel) {
      await cancelAppointment(appointmentToCancel.id);
      setCancelModalOpen(false);
      setAppointmentToCancel(null);
    }
  };

  // Close cancel modal
  const closeCancelModal = () => {
    setCancelModalOpen(false);
    setAppointmentToCancel(null);
  };

  // Open review modal
  const openReviewModal = (appointment: Appointment) => {
    setAppointmentToReview(appointment);
    setReviewModalOpen(true);
  };

  // Close review modal
  const closeReviewModal = () => {
    setReviewModalOpen(false);
    setAppointmentToReview(null);
  };

  // Submit review
  const submitReview = async (reviewData: { rating: number; comment: string }) => {
    const token = getToken();
    if (!token || !appointmentToReview) {
      throw new Error(language === 'ua' ? 'Токен авторизації відсутній' : 'Authorization token missing');
    }

    const response = await fetch('/api/v1/reviews', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        appointment_id: appointmentToReview.id,
        rating: reviewData.rating,
        comment: reviewData.comment || null,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || 'Failed to submit review');
    }

    // Refresh appointments to update the review button state
    await loadAppointments();
  };

  // Handle location click to open Google Maps route
  const handleLocationClick = (location: string) => {
    if (!location) return;

    // Try to parse the address similar to BusinessSettings
    if (location.includes(',')) {
      const parts = location.split(',').map(part => part.trim());

      if (parts.length >= 3) {
        const city = parts[0];
        const street = parts[1];
        const building = parts[2];
        openGoogleMaps(city, street, building);
      } else if (parts.length === 2) {
        const city = parts[0];
        const street = parts[1];
        openGoogleMaps(city, street, '');
      } else {
        openGoogleMaps('Ukraine', location, '');
      }
    } else {
      openGoogleMaps('Ukraine', location, '');
    }
  };

  // Handle reschedule appointment
  const handleReschedule = (appointment: Appointment) => {
    setWorkingHoursModal({
      isOpen: true,
      masterId: appointment.businessId || '',
      masterName: appointment.masterName,
      appointmentId: appointment.id,
      isReschedule: true,
    });
  };

  // Close working hours modal
  const handleCloseWorkingHours = () => {
    setWorkingHoursModal({
      isOpen: false,
      masterId: '',
      masterName: '',
      appointmentId: '',
      isReschedule: false,
    });
  };

  return (
    <div className="my-records-page">
      <div className="page-header">
        <h1 className="page-title">
          {language === 'ua' ? 'Мої записи' : 'My Records'}
        </h1>
        <p className="page-subtitle">
          {language === 'ua'
            ? 'Перегляньте свої майбутні та минулі записи до майстрів'
            : 'View your upcoming and past appointments with masters'
          }
        </p>
      </div>

      <div className="page-content">
        {/* Tab Navigation */}
        <div className="tabs-navigation">
          <button
            className={`tab-button ${activeTab === 'upcoming' ? 'active' : ''}`}
            onClick={() => setActiveTab('upcoming')}
          >
            <i className="bi bi-calendar-plus"></i>
            <span className="tab-text">
              {language === 'ua' ? 'Майбутні записи' : 'Upcoming Appointments'} ({upcomingAppointments.length})
            </span>
          </button>
          <button
            className={`tab-button ${activeTab === 'past' ? 'active' : ''}`}
            onClick={() => setActiveTab('past')}
          >
            <i className="bi bi-calendar-check"></i>
            <span className="tab-text">
              {language === 'ua' ? 'Пройдені записи' : 'Past Appointments'} ({pastAppointments.length})
            </span>
          </button>
          <button
            className={`tab-button ${activeTab === 'calendar' ? 'active' : ''}`}
            onClick={() => setActiveTab('calendar')}
          >
            <i className="bi bi-calendar3"></i>
            <span className="tab-text">
              {language === 'ua' ? 'Календарь' : 'Calendar'}
            </span>
          </button>
        </div>

        {loading ? (
          <div className="loading-state">
            <p>{language === 'ua' ? 'Завантаження...' : 'Loading...'}</p>
          </div>
        ) : activeTab === 'calendar' ? (
          <CalendarTab 
            appointments={appointments} 
            language={language} 
            colors={colors} 
            onLocationClick={handleLocationClick}
          />
        ) : currentAppointments.length === 0 ? (
          <div className="empty-state">
            {activeTab === 'upcoming' ? (
              <i className="bi bi-calendar-plus" style={{ fontSize: '3rem', color: '#ccc' }}></i>
            ) : (
              <i className="bi bi-calendar-check" style={{ fontSize: '3rem', color: '#ccc' }}></i>
            )}
            <h3>
              {activeTab === 'upcoming'
                ? (language === 'ua' ? 'Немає майбутніх записів' : 'No upcoming appointments')
                : (language === 'ua' ? 'Немає минулих записів' : 'No past appointments')
              }
            </h3>
            <p>
              {activeTab === 'upcoming'
                ? (language === 'ua'
                    ? 'Ваші майбутні записи будуть відображатися тут'
                    : 'Your upcoming appointments will appear here'
                  )
                : (language === 'ua'
                    ? 'Ваша історія записів буде відображатися тут'
                    : 'Your appointment history will appear here'
                  )
              }
            </p>
          </div>
        ) : (
          <div className="appointments-list">
            {currentAppointments.map((appointment) => (
              <div 
                key={appointment.id} 
                className={`appointment-card ${activeTab === 'upcoming' ? 'upcoming-appointment' : ''}`}
              >
                <div className="appointment-header">
                  <div className="service-info">
                    {appointment.serviceName && <h4>{appointment.serviceName}</h4>}
                    <p className="master-name">
                      {(() => {
                        if (appointment.isMasterBusiness) {
                          return <><i className="bi bi-person"></i> {appointment.masterName}</>;
                        } else {
                          return <><i className="bi bi-building"></i> {appointment.masterName}</>;
                        }
                      })()}
                    </p>
                    <p className="salon-name">
                      <i 
                        className="bi bi-geo-alt" 
                        style={{ 
                          cursor: appointment.location ? 'pointer' : 'default',
                          color: appointment.location ? colors.primary : 'inherit'
                        }}
                        onClick={() => appointment.location && handleLocationClick(appointment.location)}
                      ></i> 
                      <span 
                        style={{ 
                          cursor: appointment.location ? 'pointer' : 'default',
                          color: appointment.location ? colors.primary : 'inherit'
                        }}
                        onClick={() => appointment.location && handleLocationClick(appointment.location)}
                      >
                        {appointment.location}
                      </span>
                    </p>
                    {(appointment.masterName !== appointment.salonName ? appointment.masterPhone : appointment.businessPhone) && (
                      <p className="phone-info">
                        <i className="bi bi-telephone" style={{ color: colors.primary }}></i> {
                          (() => {
                            const phone = appointment.masterName !== appointment.salonName ? appointment.masterPhone : appointment.businessPhone;
                            const cleanPhone = phone ? phone.replace(/^\+380/, '') : '';
                            return (
                              <a 
                                href={`tel:+380${cleanPhone}`}
                                style={{ 
                                  color: colors.primary, 
                                  textDecoration: 'none',
                                  fontWeight: 500
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.location.href = `tel:+380${cleanPhone}`;
                                }}
                              >
                                {phone}
                              </a>
                            );
                          })()
                        }
                      </p>
                    )}
                  </div>
                  <div className="appointment-status">
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(appointment.status) }}
                    >
                      {getStatusText(appointment.status)}
                    </span>
                  </div>
                </div>

                <div className="appointment-details">
                  <div className="date-time">
                    <i className="bi bi-calendar"></i>
                    <span>{formatDate(appointment.date)} о {appointment.time}</span>
                  </div>
                  <div className="price">
                    <i className="bi bi-currency-dollar"></i>
                    <span>{appointment.price} ₴</span>
                  </div>
                </div>

                {appointment.notes && (
                  <div className="appointment-notes">
                    <i className="bi bi-chat-left-text"></i>
                    <span>{appointment.notes}</span>
                  </div>
                )}

                <div className="appointment-actions">
                  {appointment.status === 'upcoming' && (
                    <>
                      <button 
                        className="action-btn reschedule-btn" 
                        style={{ borderColor: colors.success, color: colors.success }}
                        onClick={() => handleReschedule(appointment)}
                      >
                        <i className="bi bi-arrow-repeat" style={{ color: colors.success }}></i>
                        {language === 'ua' ? 'Перенести' : 'Reschedule'}
                      </button>
                      {(() => {
                        const cancelStatus = canCancelAppointment(appointment);

                        if (cancelStatus.canCancel) {
                          // More than 5 hours - show cancel button with tooltip
                          return (
                            <Tooltip title={getCancellationDeadline(appointment)} arrow>
                              <button
                                className="action-btn cancel-btn"
                                onClick={() => openCancelModal(appointment)}
                              >
                                <i className="bi bi-x-circle" style={{ color: colors.error }}></i>
                                {language === 'ua' ? 'Скасувати' : 'Cancel'}
                              </button>
                            </Tooltip>
                          );
                        } else if (cancelStatus.canCancelWithContact) {
                          // 3-5 hours - show cancel button but with warning
                          return (
                            <Tooltip
                              title={
                                language === 'ua'
                                  ? 'Увага: залишилося менше 5 годин! Скасуйте зараз або доведеться звертатися до майстра.'
                                  : 'Warning: less than 5 hours remaining! Cancel now or contact the master directly.'
                              }
                              arrow
                            >
                              <button
                                className="action-btn cancel-btn warning"
                                onClick={() => openCancelModal(appointment)}
                              >
                                <i className="bi bi-x-circle" style={{ color: colors.error }}></i>
                                {language === 'ua' ? 'Скасувати' : 'Cancel'}
                              </button>
                            </Tooltip>
                          );
                        } else {
                          // Less than 3 hours - disable button
                          return (
                            <Tooltip
                              title={
                                language === 'ua'
                                  ? 'Неможливо скасувати менше ніж за 3 години до візиту. Зв\'яжіться з майстром телефоном.'
                                  : 'Cannot cancel less than 3 hours before visit. Contact master via phone.'
                              }
                              arrow
                            >
                              <span>
                                <button
                                  className="action-btn cancel-btn disabled"
                                  disabled
                                >
                                  <i className="bi bi-x-circle"></i>
                                  {language === 'ua' ? 'Скасувати' : 'Cancel'}
                                </button>
                              </span>
                            </Tooltip>
                          );
                        }
                      })()}
                    </>
                  )}
                  {appointment.status === 'completed' && (
                    <>
                      {appointment.hasReview ? (
                        <Tooltip
                          title={language === 'ua' ? 'Відгук вже надано' : 'Review already submitted'}
                          arrow
                        >
                          <span>
                            <button 
                              className="action-btn review-btn disabled"
                              disabled
                              style={{ borderColor: colors.gray, color: colors.gray }}
                            >
                              <i className="bi bi-star-fill"></i>
                              {language === 'ua' ? 'Відгук надано' : 'Review Submitted'}
                            </button>
                          </span>
                        </Tooltip>
                      ) : (
                        <button 
                          className="action-btn review-btn"
                          onClick={() => openReviewModal(appointment)}
                          style={{ borderColor: colors.primary, color: colors.primary }}
                        >
                          <i className="bi bi-star"></i>
                          {language === 'ua' ? 'Залишити відгук' : 'Leave Review'}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cancel Appointment Confirmation Modal */}
      {appointmentToCancel && (
        <CancelAppointmentModal
          isOpen={cancelModalOpen}
          onClose={closeCancelModal}
          onConfirm={handleConfirmCancel}
          appointmentDetails={{
            serviceName: appointmentToCancel.serviceName,
            masterName: appointmentToCancel.masterName,
            date: appointmentToCancel.date,
            time: appointmentToCancel.time
          }}
        />
      )}

      {/* Review Modal */}
      {appointmentToReview && (
        <ReviewModal
          isOpen={reviewModalOpen}
          onClose={closeReviewModal}
          onSubmit={submitReview}
          appointmentDetails={{
            serviceName: appointmentToReview.serviceName,
            masterName: appointmentToReview.masterName,
            businessName: appointmentToReview.salonName,
            appointmentId: appointmentToReview.id
          }}
        />
      )}

      {/* Working Hours Modal for Reschedule */}
      <WorkingHoursModal
        isOpen={workingHoursModal.isOpen}
        onClose={handleCloseWorkingHours}
        masterId={workingHoursModal.masterId}
        masterName={workingHoursModal.masterName}
        appointmentId={workingHoursModal.appointmentId}
        isReschedule={workingHoursModal.isReschedule}
        onRescheduleSuccess={loadAppointments}
      />
    </div>
  );
};

export default MyRecordsPage;
